import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { COLORS, FONTS } from "@/lib/constants";
import { INK, MUTED_36, FONT as YFONT, ROOMS } from "@/lib/yuoDesign";
import StoneOrb from "@/components/yuo/StoneOrb";
import { useResponsive } from "@/lib/responsive";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Full-text reader for Bible / Aurelius with margin notes and verse-breakdown
 * from Yuo…theologian.
 */
export default function LibraryRead() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { readingMaxWidth, gutter, isPhone } = useResponsive();
  const [notesView, setNotesView] = useState(false);

  const source = params.get("source") || "bible";
  const code = params.get("code") || "GEN";
  const chapter = parseInt(params.get("chapter") || "1", 10);
  const lang = (params.get("lang") || "en").toLowerCase();

  const [loading, setLoading] = useState(true);
  const [chapterData, setChapterData] = useState(null);
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [notes, setNotes] = useState([]);
  const [limits, setLimits] = useState(null);

  const isPro = user?.plan === "pro" || user?.plan === "master";

  const loadChapter = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.fullChapter(source, code, chapter, lang);
      setChapterData(data);
    } catch (e) {
      console.warn("chapter load", e);
    } finally {
      setLoading(false);
    }
  }, [source, code, chapter, lang]);

  useEffect(() => {
    loadChapter();
  }, [loadChapter]);

  useEffect(() => {
    (async () => {
      try {
        const [lim, marginNotes] = await Promise.all([api.libraryLimits(), api.listMarginNotes()]);
        setLimits(lim);
        setNotes(marginNotes?.notes || marginNotes || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const goChapter = (n) => {
    if (!chapterData) return;
    if (n < 1) return;
    if (chapterData.chapter_count && n > chapterData.chapter_count) return;
    const p = new URLSearchParams(params);
    p.set("chapter", String(n));
    setParams(p, { replace: true });
    setSelectedVerse(null);
    setBreakdown(null);
  };

  const changeLang = (nextLang) => {
    const p = new URLSearchParams(params);
    p.set("lang", nextLang);
    setParams(p, { replace: true });
  };

  const runBreakdown = async (v) => {
    setBreakdownLoading(true);
    setBreakdown(null);
    try {
      const res = await api.verseBreakdown({
        source,
        code,
        chapter,
        verse: v.verse,
        lang,
      });
      setBreakdown(res);
      // refresh limits
      const lim = await api.libraryLimits();
      setLimits(lim);
    } catch (e) {
      setBreakdown({ error: e?.message || "Could not run breakdown" });
    } finally {
      setBreakdownLoading(false);
    }
  };

  const saveNote = async () => {
    if (!selectedVerse || !noteDraft.trim()) return;
    try {
      await api.createMarginNote({
        source,
        code,
        chapter,
        verse: selectedVerse.verse,
        lang,
        note: noteDraft.trim(),
      });
      const marginNotes = await api.listMarginNotes();
      setNotes(marginNotes?.notes || marginNotes || []);
      setNoteDraft("");
    } catch (e) {
      alert(e?.message || "Could not save note");
    }
  };

  const versesNotes = useMemo(() => {
    const map = {};
    (notes || []).forEach((n) => {
      if (n.source === source && n.code === code && n.chapter === chapter) {
        map[n.verse] = map[n.verse] || [];
        map[n.verse].push(n);
      }
    });
    return map;
  }, [notes, source, code, chapter]);

  return (
    <div
      data-testid="library-read-screen"
      style={{ minHeight: "100vh", background: "radial-gradient(30% 23% at 50% 11%, rgba(var(--role-green-rgb),0.11), transparent 86%), radial-gradient(92% 50% at 50% 96%, rgba(var(--role-yellow-rgb),0.11), transparent 90%), var(--yuo-bg)", display: "flex", flexDirection: "column", fontFamily: '\"JetBrains Mono\", ui-monospace, monospace' }}
    >
      <div
        style={{
          display: isPhone ? "grid" : "flex",
          gridTemplateColumns: isPhone ? "1fr auto" : undefined,
          alignItems: "center",
          justifyContent: "space-between",
          padding: isPhone ? "6px 18px 10px" : `14px ${gutter}px`,
          gap: isPhone ? "10px 8px" : 12,
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={() => navigate("/library")}
          data-testid="library-read-back"
          style={{
            display: "inline-flex", alignItems: "center", gap: 10,
            background: "transparent", border: "none", padding: 0, cursor: "pointer",
            color: isPhone ? INK : "var(--fg-75)", fontFamily: YFONT, fontSize: isPhone ? 13 : 11,
            letterSpacing: isPhone ? "0.3em" : "0.24em", textTransform: "uppercase",
            gridRow: isPhone ? 1 : undefined, gridColumn: isPhone ? 1 : undefined,
          }}
        >
          <StoneOrb color="library" size={isPhone ? 12 : 14} />
          <span>Library</span>
        </button>
        {isPhone && (
          <div data-testid="read-view-segment" style={{ gridRow: 2, gridColumn: 2, display: "flex", gap: 4, padding: 3, borderRadius: 999, background: "var(--m-more-bg)" }}>
            {[["text", "Text"], ["notes", `Notes · ${Object.values(versesNotes).reduce((a, l) => a + (l?.length || 0), 0)}`]].map(([k, label]) => {
              const active = (k === "notes") === notesView;
              return (
                <button key={k} type="button" data-testid={`read-view-${k}`} onClick={() => setNotesView(k === "notes")} style={{
                  height: 34, padding: "0 12px", borderRadius: 999, border: "none", cursor: "pointer", fontFamily: YFONT,
                  fontSize: 9.5, letterSpacing: "0.18em", textTransform: "uppercase", whiteSpace: "nowrap",
                  background: active ? "radial-gradient(120% 160% at 30% 0%, rgba(var(--role-yellow-rgb),0.34), rgba(var(--role-yellow-rgb),0.08))" : "transparent",
                  color: active ? "var(--lit-yellow-20)" : COLORS.textSecondary, boxShadow: "none",
                }}>{label}</button>
              );
            })}
          </div>
        )}
        <div style={{
          gridRow: isPhone ? 2 : undefined, gridColumn: isPhone ? 1 : undefined, justifySelf: isPhone ? "start" : undefined,
          display: "inline-flex", alignItems: "center", gap: 10,
          padding: "7px 18px",
          borderRadius: 999,
          background: "radial-gradient(120% 160% at 30% 0%, rgba(var(--role-yellow-rgb),0.22), rgba(var(--role-yellow-rgb),0.05))",
          boxShadow: "0 0 22px rgba(var(--role-yellow-rgb),0.20)",
        }}>
          <span style={{ color: INK, fontFamily: YFONT, fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase" }}>
            {chapterData?.book_name || code}
          </span>
          <span style={{ color: MUTED_36, fontFamily: YFONT, fontSize: 10, letterSpacing: "0.2em" }}>
            · CH {chapter}{chapterData?.chapter_count ? ` / ${chapterData.chapter_count}` : ""}
          </span>
        </div>
        <div style={{ display: "flex", gap: isPhone ? 4 : 6, gridRow: isPhone ? 1 : undefined, gridColumn: isPhone ? 2 : undefined, padding: isPhone ? 3 : 0, borderRadius: 999, background: isPhone ? "var(--m-more-bg)" : "transparent" }}>
          {(() => {
            const langsBySource = {
              bible: ["ru", "en", "pt"],
              torah: ["en", "ru", "he"],
              quran: ["en", "ru", "ar"],
              aurelius: ["en", "ru"],
              tao_te_ching: ["en", "zh"],
              dhammapada: ["en", "pi"],
              bhagavad_gita: ["en", "sa", "sa-latn"],
            };
            const langs = chapterData?.languages_available || langsBySource[source] || ["ru", "en"];
            return langs.map((l) => {
              const active = lang === l;
              return (
                <button
                  key={l}
                  onClick={() => changeLang(l)}
                  data-testid={`lang-${l}`}
                  style={{
                    padding: isPhone ? 0 : "5px 12px",
                    width: isPhone ? 40 : undefined, height: isPhone ? 36 : undefined,
                    borderRadius: 999,
                    border: "none",
                    background: active
                      ? "radial-gradient(120% 160% at 30% 0%, rgba(var(--role-yellow-rgb),0.34), rgba(var(--role-yellow-rgb),0.08))"
                      : (isPhone ? "transparent" : "var(--fg-05)"),
                    boxShadow: active ? "0 0 20px rgba(var(--role-yellow-rgb),0.32)" : "none",
                    color: active ? "var(--lit-yellow-20)" : COLORS.textSecondary,
                    fontFamily: YFONT,
                    fontSize: 11,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                >
                  {l}
                </button>
              );
            });
          })()}
        </div>
      </div>

      <div className={isPhone ? "m-scroll" : undefined} style={{ flex: 1, overflowY: "auto", padding: isPhone ? "8px 14px calc(30px + env(safe-area-inset-bottom, 0px))" : `24px ${gutter}px 96px` }}>
        <div style={{ maxWidth: readingMaxWidth, margin: "0 auto" }}>
          {isPhone && notesView ? (
            <div data-testid="read-notes-list" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {Object.entries(versesNotes).flatMap(([vn, list]) => (list || []).map((n) => ({ ...n, vn: Number(vn) })))
                .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                .map((n, i) => {
                  const verse = (chapterData?.verses || []).find((v) => v.verse === n.vn);
                  return (
                    <div key={n.note_id || i} data-testid="read-note-card" style={{ padding: "14px 16px", borderRadius: 22, background: "var(--fg-04)", display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <span style={{ color: "var(--lit-yellow-20)", fontFamily: YFONT, fontSize: 9.5, letterSpacing: "0.22em", textTransform: "uppercase" }}>{(chapterData?.book_name || code).slice(0, 3)} {chapter}:{n.vn}</span>
                        <span style={{ color: COLORS.textMuted, fontFamily: YFONT, fontSize: 9.5 }}>{n.created_at ? new Date(n.created_at).toLocaleDateString() : ""}</span>
                      </div>
                      {verse && <div style={{ color: COLORS.textSecondary, fontFamily: YFONT, fontSize: 12, lineHeight: 1.6, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{verse.text}</div>}
                      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#a26bff", flex: "none", marginTop: 6 }} />
                        <span style={{ color: INK, fontFamily: YFONT, fontSize: 13, lineHeight: 1.65 }}>{n.note}</span>
                      </div>
                    </div>
                  );
                })}
              {Object.values(versesNotes).every((l) => !l?.length) && <div style={{ color: COLORS.textMuted, padding: 30, textAlign: "center", fontFamily: YFONT, fontSize: 12 }}>No notes in this chapter yet.</div>}
            </div>
          ) : loading ? (
            <div style={{ color: COLORS.textMuted, padding: 40, textAlign: "center" }}>Loading chapter…</div>
          ) : !chapterData ? (
            <div style={{ color: COLORS.textMuted, padding: 40, textAlign: "center" }}>Not found.</div>
          ) : (
            <>
              {(chapterData.verses || []).map((v) => {
                const isSel = selectedVerse?.verse === v.verse;
                const hasNote = !!versesNotes[v.verse]?.length;
                const isRtl = lang === "he" || lang === "ar";
                const isCJK = lang === "zh";
                const isDevanagari = lang === "sa" || lang === "pi";
                const isLargeScript = isRtl || isCJK || isDevanagari;
                return (
                  <div
                    key={v.verse}
                    id={`verse-${v.verse}`}
                    onClick={() => setSelectedVerse(v)}
                    data-testid={`verse-${v.verse}`}
                    dir={isRtl ? "rtl" : "ltr"}
                    data-lang={lang}
                    style={{
                      display: "flex",
                      flexDirection: isRtl ? "row-reverse" : "row",
                      gap: 12,
                      padding: "10px 6px",
                      borderRadius: 8,
                      cursor: "pointer",
                      background: isSel ? `rgba(var(--role-yellow-rgb),0.07)` : "transparent",
                      borderLeft: isSel && !isRtl ? `2px solid ${COLORS.library}` : "2px solid transparent",
                      borderRight: isSel && isRtl ? `2px solid ${COLORS.library}` : "2px solid transparent",
                      transition: "background 0.15s",
                    }}
                  >
                    <div
                      style={{
                        color: COLORS.library,
                        fontFamily: FONTS.body,
                        fontSize: 11,
                        fontWeight: 600,
                        minWidth: 24,
                        paddingTop: 4,
                        textAlign: isRtl ? "right" : "left",
                      }}
                    >
                      {v.verse}
                    </div>
                    <div
                      style={{
                        color: COLORS.textPrimary,
                        fontSize: isLargeScript ? 20 : 17,
                        lineHeight: isLargeScript ? "34px" : "27px",
                        flex: 1,
                        textAlign: isRtl ? "right" : "left",
                      }}
                    >
                      {v.text}
                      {hasNote && (
                        <span
                          style={{
                            marginLeft: 8,
                            color: COLORS.diary,
                            fontSize: 12,
                          }}
                          title="You have a margin note here"
                        >
                          ✎
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 40, gap: 12, flexWrap: "wrap" }}>
                <button
                  onClick={() => goChapter(chapter - 1)}
                  disabled={chapter <= 1}
                  data-testid="prev-chapter"
                  style={chapterNavBtn(chapter <= 1)}
                >
                  <StoneOrb color="library" size={12} />
                  <span>Prev · CH {Math.max(1, chapter - 1)}</span>
                </button>
                <button
                  onClick={() => goChapter(chapter + 1)}
                  disabled={chapterData?.chapter_count && chapter >= chapterData.chapter_count}
                  data-testid="next-chapter"
                  style={chapterNavBtn(chapterData?.chapter_count && chapter >= chapterData.chapter_count)}
                >
                  <span>Next · CH {chapter + 1}</span>
                  <StoneOrb color="library" size={12} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {selectedVerse && (
        <div className="yuo-modal-backdrop" onClick={() => { setSelectedVerse(null); setBreakdown(null); setNoteDraft(""); }}>
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="verse-drawer"
            className={isPhone ? "m-sheet-panel m-scroll" : undefined}
            style={isPhone ? {
              position: "absolute", left: 0, right: 0, bottom: 0, maxHeight: "84%",
              background: "radial-gradient(80% 60% at 20% 0%, rgba(var(--role-yellow-rgb),0.14), transparent 72%), var(--yuo-bg)",
              padding: "14px 16px calc(30px + env(safe-area-inset-bottom, 0px))", overflowY: "auto",
              borderRadius: "32px 32px 0 0", boxShadow: "0 -20px 60px rgba(var(--role-yellow-rgb),0.16)",
            } : {
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: "min(480px, 92vw)",
              background: "radial-gradient(80% 60% at 20% 0%, rgba(var(--role-yellow-rgb),0.14), transparent 72%), var(--yuo-bg)",
              padding: 24,
              overflowY: "auto",
              borderTopLeftRadius: 30,
              borderBottomLeftRadius: 30,
              boxShadow: "-20px 0 60px rgba(var(--role-yellow-rgb),0.16)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 10,
                padding: "6px 14px", borderRadius: 999,
                background: "radial-gradient(120% 160% at 30% 0%, rgba(var(--role-yellow-rgb),0.20), rgba(var(--role-yellow-rgb),0.04))",
                boxShadow: "0 0 18px rgba(var(--role-yellow-rgb),0.16)",
              }}>
                <StoneOrb color="library" size={12} />
                <span style={{ color: "var(--lit-yellow-20)", fontFamily: YFONT, fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase" }}>
                  {chapterData?.book_name} {chapter}:{selectedVerse.verse}
                </span>
              </div>
              <button
                onClick={() => { setSelectedVerse(null); setBreakdown(null); }}
                data-testid="verse-close"
                aria-label="Close"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: "transparent", border: "none", padding: 0, cursor: "pointer",
                  color: "var(--fg-55)", fontFamily: YFONT, fontSize: 10,
                  letterSpacing: "0.24em", textTransform: "uppercase",
                }}
              >
                <StoneOrb color="library" size={12} />
                <span>Close</span>
              </button>
            </div>
            <div
              style={{
                color: COLORS.textPrimary,
                fontFamily: FONTS.heading,
                fontSize: 18,
                lineHeight: "28px",
                marginBottom: 24,
                padding: "16px 18px",
                borderRadius: 22,
                background: "var(--fg-04)",
                boxShadow: "0 0 32px rgba(var(--role-yellow-rgb),0.10) inset",
              }}
            >
              {selectedVerse.text}
            </div>

            <button
              onClick={() => runBreakdown(selectedVerse)}
              disabled={breakdownLoading}
              data-testid="verse-breakdown-btn"
              style={{
                display: "inline-flex", alignItems: "center", gap: 12,
                background: "radial-gradient(120% 160% at 20% 0%, rgba(var(--role-yellow-rgb),0.34), rgba(var(--role-yellow-rgb),0.08))",
                boxShadow: "0 0 30px rgba(var(--role-yellow-rgb),0.28)",
                color: "var(--lit-yellow-20)",
                borderRadius: 999,
                padding: "12px 22px",
                fontFamily: YFONT,
                fontSize: 11,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                border: "none",
                marginBottom: 20,
                opacity: breakdownLoading ? 0.6 : 1,
                cursor: breakdownLoading ? "default" : "pointer",
              }}
            >
              <StoneOrb color="library" size={14} />
              {breakdownLoading ? "Yuo…theologian is reading…" : "Ask Yuo…theologian"}
            </button>

            {breakdown && !breakdown.error && (
              <div
                data-testid="breakdown-result"
                style={{
                  padding: "20px 22px",
                  borderRadius: 26,
                  background:
                    "radial-gradient(120% 100% at 22% 0%, rgba(var(--role-yellow-rgb),0.16), rgba(var(--role-yellow-rgb),0.03) 62%, transparent 100%)",
                  boxShadow:
                    "0 0 40px rgba(var(--role-yellow-rgb),0.14) inset",
                  color: COLORS.textPrimary,
                  fontFamily: FONTS.body,
                  fontSize: 14,
                  lineHeight: "22px",
                  whiteSpace: "pre-wrap",
                  marginBottom: 20,
                }}
              >
                {breakdown.breakdown || breakdown.content || JSON.stringify(breakdown)}
              </div>
            )}
            {breakdown?.error && (
              <div style={{ color: COLORS.error, fontFamily: FONTS.body, fontSize: 13, marginBottom: 20 }}>
                {breakdown.error}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <StoneOrb color="diary" size={10} />
              <div style={{ color: MUTED_36, fontFamily: YFONT, fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase" }}>
                Margin notes
              </div>
            </div>
            {(versesNotes[selectedVerse.verse] || []).map((n) => (
              <div
                key={n.note_id || n._id}
                style={{
                  padding: "14px 18px",
                  background: "radial-gradient(120% 100% at 22% 0%, rgba(var(--role-purple-rgb),0.14), rgba(var(--role-purple-rgb),0.03) 62%, transparent 100%)",
                  boxShadow: "0 0 28px rgba(var(--role-purple-rgb),0.12) inset",
                  borderRadius: 20,
                  marginBottom: 10,
                }}
              >
                <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.body, fontSize: 13, lineHeight: "20px", whiteSpace: "pre-wrap" }}>
                  {n.note}
                </div>
                <div style={{ color: MUTED_36, fontFamily: YFONT, fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", marginTop: 6 }}>
                  {n.updated_at ? new Date(n.updated_at).toLocaleDateString() : ""}
                </div>
              </div>
            ))}
            <textarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Write a margin note…"
              data-testid="note-draft"
              rows={3}
              style={{
                width: "100%",
                boxSizing: "border-box",
                background: "var(--fg-04)",
                border: "none",
                borderRadius: 20,
                padding: "14px 18px",
                color: COLORS.textPrimary,
                fontFamily: FONTS.body,
                fontSize: 14,
                lineHeight: "22px",
                outline: "none",
                boxShadow: "0 0 30px rgba(var(--role-purple-rgb),0.12) inset",
                marginTop: 6,
                display: "block",
                resize: "vertical",
              }}
            />
            <button
              onClick={saveNote}
              disabled={!noteDraft.trim()}
              data-testid="save-note"
              style={{
                display: "inline-flex", alignItems: "center", gap: 10,
                marginTop: 12,
                background: "radial-gradient(120% 160% at 30% 0%, rgba(var(--role-purple-rgb),0.34), rgba(var(--role-purple-rgb),0.08))",
                boxShadow: "0 0 24px rgba(var(--role-purple-rgb),0.28)",
                color: "var(--yuo-ink)",
                borderRadius: 999,
                padding: "10px 22px",
                border: "none",
                fontFamily: YFONT,
                fontSize: 11,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                opacity: noteDraft.trim() ? 1 : 0.5,
                cursor: noteDraft.trim() ? "pointer" : "default",
              }}
            >
              <StoneOrb color="diary" size={12} />
              Save note
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const chapterNavBtn = (disabled) => ({
  display: "inline-flex", alignItems: "center", gap: 10,
  border: "none",
  borderRadius: 999,
  padding: "10px 18px",
  color: "var(--fg-75)",
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: 11,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  background: "radial-gradient(120% 160% at 30% 0%, rgba(var(--role-yellow-rgb),0.20), rgba(var(--role-yellow-rgb),0.04))",
  boxShadow: "0 0 22px rgba(var(--role-yellow-rgb),0.16)",
  opacity: disabled ? 0.3 : 1,
  cursor: disabled ? "not-allowed" : "pointer",
});
